import { Suspense, useRef } from 'react';
// SC-1213: the component registry arrives as a prop rather than an import, so the
// server can supply a statically-imported map that `renderToString` can actually
// render. Importing a map here would pin every caller to the lazy one again.
import { GeneratedColumn, GeneratedRow } from './contracts';
import { CmsComponent } from '../contracts';
import { ColumnAlignment, ColumnDirection, LayoutElementStyle } from '@repo/types';
import { useSchedulerStatus, isComponentVisible } from './SchedulerContext';
import { buildColumnStyle, buildElementStyle } from './layoutStyles';
import { generateGradientCSS } from '../../utils/gradient';

const IMAGE_COMPONENTS = new Set<string>([
  CmsComponent.ArticleList,
  CmsComponent.ArticleCarousel,
  CmsComponent.ArticleHighlightedCard,
  CmsComponent.VideoList,
  CmsComponent.VideoCarousel,
  CmsComponent.PhotoAlbums,
]);

/** A gradient wins over a background image; both are expressed as `background-image`. */
const resolveBackgroundImage = (style: LayoutElementStyle | undefined): string | undefined =>
  generateGradientCSS(style?.gradient) || (style?.backgroundImage ? `url(${style.backgroundImage})` : undefined);

export default function Row({ id, tree, isAboveFold, componentsMap }: GeneratedRow) {
  const row = tree.rows[id]!;
  const rowStyle = row.style;
  // Row-scoped ref so only the first image component across ALL columns gets priority
  const hasPrioritizedRef = useRef(false);
  hasPrioritizedRef.current = false;

  const hasBackground = !!(rowStyle?.backgroundColor || rowStyle?.backgroundImage || rowStyle?.gradient);

  return (
    <div
      className={`slc-row${hasBackground ? ' slc-row--has-background' : ''}`}
      style={buildElementStyle(rowStyle, resolveBackgroundImage(rowStyle))}
    >
      {row.columnIds.map((columnId) => (
        <Column
          key={columnId}
          id={columnId}
          tree={tree}
          isAboveFold={isAboveFold}
          hasPrioritizedRef={hasPrioritizedRef}
          componentsMap={componentsMap}
        />
      ))}
    </div>
  );
}

function Column({
  id,
  tree,
  isAboveFold,
  hasPrioritizedRef,
  componentsMap,
}: GeneratedColumn & { isAboveFold?: boolean; hasPrioritizedRef: React.MutableRefObject<boolean> }) {
  const column = tree.columns[id]!;
  const direction = column.direction ?? ColumnDirection.Bottom;
  const alignment = column.alignment ?? ColumnAlignment.Center;
  const columnStyle = column.style;
  const schedulerStatus = useSchedulerStatus();

  const hasBackground = !!(columnStyle?.backgroundColor || columnStyle?.backgroundImage || columnStyle?.gradient);

  let justifyContent = 'center';
  if (alignment === ColumnAlignment.Left) {
    justifyContent = 'flex-start';
  } else if (alignment === ColumnAlignment.Right) {
    justifyContent = 'flex-end';
  }

  const columnContent = column.childrenIds.map((childId) => {
    if (tree.components[childId]) {
      const componentData = tree.components[childId];
      if (!componentData) {
        return null;
      }

      // Check if component should be visible based on scheduler config
      if (!isComponentVisible(componentData.scheduler, componentData.isEnabled, schedulerStatus)) {
        return null;
      }

      const Component = componentsMap[componentData.type];
      if (!Component) {
        return null;
      }
      const props = {
        ...componentData,
        ...(componentData.data ?? {}),
      };

      // Auto-prioritize the first image-bearing component in above-fold rows
      // to ensure eager loading and fetchpriority="high" for the LCP image
      if (isAboveFold && !hasPrioritizedRef.current && IMAGE_COMPONENTS.has(componentData.type)) {
        props.prioritize = true;
        hasPrioritizedRef.current = true;
      }

      // @ts-ignore
      return (
        <Suspense key={childId} fallback={<div style={{ minHeight: 200 }} />}>
          <Component {...(props as any)} />
        </Suspense>
      );
    } else if (tree.rows[childId]) {
      return <Row key={childId} id={childId} tree={tree} isAboveFold={isAboveFold} componentsMap={componentsMap} />;
    }
  });

  // When fitToSiblings is enabled, use a wrapper structure so the column doesn't expand the row
  if (column.fitToSiblings) {
    return (
      <div
        key={id}
        className="slc-column-wrapper slc-column-wrapper--fit-to-siblings"
        style={{ gridColumn: `span ${column.size}` }}
      >
        <div
          className={`slc-column slc-column--fit-to-siblings${hasBackground ? ' slc-column--has-background' : ''}`}
          style={buildColumnStyle(columnStyle, resolveBackgroundImage(columnStyle), direction, justifyContent)}
        >
          {columnContent}
        </div>
      </div>
    );
  }

  return (
    <div
      key={id}
      className={`slc-column${hasBackground ? ' slc-column--has-background' : ''}`}
      style={{
        gridColumn: `span ${column.size}`,
        ...buildColumnStyle(columnStyle, resolveBackgroundImage(columnStyle), direction, justifyContent),
      }}
    >
      {columnContent}
    </div>
  );
}
